import avatar from "../../assets/images/avatar.webp";
import avatarScroll from "../../assets/images/avatar2.webp";
import ProjectModal from "../ProjectModal/ProjectModal";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PROJECTS from "./projectsData";
import ProjectCard from "./ProjectCard";
import TypingIntro from "./TypingIntro";
import useInView from "../../hooks/useInView";
import { pushToDataLayer } from "../../utils/analytics";
import "./Main.scss";

function getVisibleCards() {
  if (typeof window === "undefined") return 1;
  if (window.innerWidth >= 1200) return 3;
  if (window.innerWidth >= 768) return 2;
  return 1;
}

function Main() {
  const modalOpenedAtRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [offsetY, setOffsetY] = useState(
    typeof window !== "undefined" ? window.pageYOffset : 0,
  );
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(getVisibleCards);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);

  const totalProjects = PROJECTS.length;

  const modalHandler = useCallback((project) => {
    modalOpenedAtRef.current = Date.now();

    pushToDataLayer("project_open", {
      project_id: project.id,
      project_name: project.name,
    });

    setSelectedProject(project);
  }, []);

  const closeProjectModal = useCallback(() => {
    if (selectedProject && modalOpenedAtRef.current) {
      const modalTimeMs = Date.now() - modalOpenedAtRef.current;

      pushToDataLayer("project_close", {
        project_id: selectedProject.id,
        project_name: selectedProject.name,
        modal_time_ms: modalTimeMs,
      });
    }

    modalOpenedAtRef.current = null;
    setSelectedProject(null);
  }, [selectedProject]);

  const goToPrevProject = useCallback(() => {
    if (isAnimating || totalProjects <= visibleCards) return;
    setSlideDirection("prev");
    setIsAnimating(true);
  }, [isAnimating, totalProjects, visibleCards]);

  const goToNextProject = useCallback(() => {
    if (isAnimating || totalProjects <= visibleCards) return;
    setSlideDirection("next");
    setIsAnimating(true);
  }, [isAnimating, totalProjects, visibleCards]);

  const visibleProjects = useMemo(() => {
    if (visibleCards >= totalProjects) return PROJECTS;

    return Array.from({ length: visibleCards }, (_, i) => {
      return PROJECTS[(currentProjectIndex + i) % totalProjects];
    });
  }, [currentProjectIndex, visibleCards, totalProjects]);

  const carouselProjects = useMemo(() => {
    if (visibleCards >= totalProjects) {
      return PROJECTS.map((project, index) => ({
        key: `static-${project.id}-${index}`,
        project,
        isInteractive: true,
      }));
    }

    const prevProject =
      PROJECTS[(currentProjectIndex - 1 + totalProjects) % totalProjects];
    const nextProject =
      PROJECTS[(currentProjectIndex + visibleCards) % totalProjects];

    const currentSlides = visibleProjects.map((project, index) => ({
      key: `current-${project.id}-${currentProjectIndex}-${index}`,
      project,
      isInteractive: true,
    }));

    return [
      {
        key: `clone-prev-${prevProject.id}-${currentProjectIndex}`,
        project: prevProject,
        isInteractive: false,
      },
      ...currentSlides,
      {
        key: `clone-next-${nextProject.id}-${currentProjectIndex}`,
        project: nextProject,
        isInteractive: false,
      },
    ];
  }, [currentProjectIndex, totalProjects, visibleCards, visibleProjects]);

  const handleTrackTransitionEnd = useCallback(() => {
    if (!isAnimating) return;

    setCurrentProjectIndex((prev) => {
      if (slideDirection === "next") {
        return (prev + 1) % totalProjects;
      }

      if (slideDirection === "prev") {
        return (prev - 1 + totalProjects) % totalProjects;
      }

      return prev;
    });

    setIsAnimating(false);
    setSlideDirection(null);
  }, [isAnimating, slideDirection, totalProjects]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setOffsetY(window.pageYOffset);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const desktopMq = window.matchMedia("(min-width: 1200px)");
    const tabletMq = window.matchMedia("(min-width: 768px)");

    const updateVisibleCards = () => {
      if (desktopMq.matches) {
        setVisibleCards(3);
      } else if (tabletMq.matches) {
        setVisibleCards(2);
      } else {
        setVisibleCards(1);
      }
    };

    updateVisibleCards();

    desktopMq.addEventListener("change", updateVisibleCards);
    tabletMq.addEventListener("change", updateVisibleCards);

    return () => {
      desktopMq.removeEventListener("change", updateVisibleCards);
      tabletMq.removeEventListener("change", updateVisibleCards);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const preloadIndices = new Set();

    for (let i = -1; i <= visibleCards + 1; i += 1) {
      preloadIndices.add(
        (currentProjectIndex + i + totalProjects) % totalProjects,
      );
    }

    const preloadImages = () => {
      preloadIndices.forEach((index) => {
        const img = new Image();
        img.src = PROJECTS[index].image;
      });

      const avatarScrollImage = new Image();
      avatarScrollImage.src = avatarScroll;
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(preloadImages);
    } else {
      setTimeout(preloadImages, 0);
    }
  }, [currentProjectIndex, visibleCards, totalProjects]);

  const [skillsRef, skillsVisible] = useInView({ threshold: 0.1 });
  const [aboutRef, aboutVisible] = useInView({ threshold: 0.1 });
  const [contactRef, contactVisible] = useInView({ threshold: 0.1 });

  const homeAvatarClass =
    offsetY < 10 ? "main__avatar" : "main__avatar--scroll";

  const carouselTrackClass = [
    "main__carousel-track",
    isAnimating ? "main__carousel-track--animating" : "",
    slideDirection === "next" ? "main__carousel-track--next" : "",
    slideDirection === "prev" ? "main__carousel-track--prev" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const displayStart = totalProjects === 0 ? 0 : currentProjectIndex + 1;

  return (
    <main className="main">
      <section
        id="home"
        className="main__section"
        aria-labelledby="home-heading"
      >
        <h1 id="home-heading" className="sr-only">
          Home
        </h1>

        <TypingIntro />

        <img
          className={homeAvatarClass}
          src={offsetY < 10 ? avatar : avatarScroll}
          alt="Illustrated avatar of Chanho Kim"
          width={270} 
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </section>

      <section
        className="main__section main__section--projects"
        id="projects"
        aria-labelledby="projects-heading"
      >
        <h2 className="main__title" id="projects-heading">
          Projects
        </h2>

        <div className="main__projects-shell">
          <div
            className="main__cards main__cards--desktop"
            role="list"
            aria-hidden={visibleCards < 3}
          >
            {PROJECTS.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={modalHandler}
                loading={index < 3 ? "eager" : "lazy"}
                fetchPriority={index < 1 ? "high" : "auto"}
                isInteractive
              />
            ))}
          </div>

          <div
            className="main__carousel"
            role="region"
            aria-label="Featured projects carousel"
            aria-hidden={visibleCards === 3}
            style={{ "--visible-cards": visibleCards }}
          >
            <div className="main__carousel-row">
              <button
                type="button"
                className="main__carousel-btn main__carousel-btn--side"
                onClick={goToPrevProject}
                aria-label="Show previous project"
                disabled={isAnimating}
              >
                <FiChevronLeft aria-hidden="true" />
              </button>

              <div className="main__carousel-viewport">
                <div
                  className={carouselTrackClass}
                  onTransitionEnd={handleTrackTransitionEnd}
                >
                  {carouselProjects.map((slide, index) => {
                    const isClone =
                      index === 0 || index === carouselProjects.length - 1;

                    return (
                      <div
                        key={slide.key}
                        className="main__carousel-slide"
                        aria-hidden={isClone}
                      >
                        <ProjectCard
                          project={slide.project}
                          onOpen={modalHandler}
                          loading={index <= visibleCards ? "eager" : "lazy"}
                          fetchPriority={index === 1 ? "high" : "auto"}
                          isInteractive={slide.isInteractive}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                className="main__carousel-btn main__carousel-btn--side"
                onClick={goToNextProject}
                aria-label="Show next project"
                disabled={isAnimating}
              >
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>

            <p className="main__carousel-status" aria-live="polite">
              Showing {displayStart} of {PROJECTS.length}
            </p>
          </div>
        </div>
      </section>

      <section
        className="main__section"
        id="skills"
        aria-labelledby="skills-heading"
      >
        <h2 className="main__title" id="skills-heading">
          Skills
        </h2>

        <div
          ref={skillsRef}
          className={`main__reveal ${skillsVisible ? "main__reveal--visible" : ""}`}
        >
          <dl className="main__skills" role="list">
            <div className="main__skills-group" role="listitem">
              <dt className="main__skills-label">Frontend</dt>
              <dd className="main__skills-list">
                React, Next.js, JavaScript (ES6+), TypeScript, HTML, CSS, Sass,
                Bootstrap
              </dd>
            </div>

            <div className="main__skills-group" role="listitem">
              <dt className="main__skills-label">Backend</dt>
              <dd className="main__skills-list">
                Node.js, Express, REST APIs, OAuth, MySQL
              </dd>
            </div>

            <div className="main__skills-group" role="listitem">
              <dt className="main__skills-label">Analytics &amp; Data</dt>
              <dd className="main__skills-list">
                Google Analytics (GA4), Google Tag Manager, Looker Studio, BigQuery, Data
                Layer Design
              </dd>
            </div>

            <div className="main__skills-group" role="listitem">
              <dt className="main__skills-label">Automation &amp; Tools</dt>
              <dd className="main__skills-list">
                Node.js scripts, Git, GitHub, CI/CD, Cloudflare
              </dd>
            </div>

            <div className="main__skills-group" role="listitem">
              <dt className="main__skills-label">Other</dt>
              <dd className="main__skills-list">
                Accessibility (WCAG/AODA), Chrome Extensions, ArcGIS
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        className="main__section"
        id="about"
        aria-labelledby="about-heading"
      >
        <h2 className="main__title" id="about-heading">
          About Me
        </h2>

        <div
          ref={aboutRef}
          className={`main__reveal ${aboutVisible ? "main__reveal--visible" : ""}`}
        >
          <p className="main__description">
            Full stack developer with 4+ years of experience delivering
            production-ready web applications at scale.
          </p>
          <p className="main__description">
            Built and maintained 140+ courses for Ontario’s public education
            platform (TVO), while improving developer efficiency through
            automation and workflow optimization.
          </p>
          <p className="main__description">
            Strong focus on analytics-driven development — implementing GA4,
            GTM, and data tracking systems to turn user behavior into actionable
            insights.
          </p>
          <p className="main__description">
            I build with both engineering and business impact in mind.
          </p>
        </div>
      </section>

      <section
        className="main__section"
        id="contact"
        aria-labelledby="contact-heading"
      >
        <h2 className="main__title" id="contact-heading">
          Contact
        </h2>

        <div
          ref={contactRef}
          className={`main__reveal ${contactVisible ? "main__reveal--visible" : ""}`}
        >
          <p className="main__contact-heading">
            Let's build something together.
          </p>
          <p className="main__contact-subtext">
            Whether it's a new project, a role, or just a conversation about
            analytics and web performance — I'd love to hear from you.
          </p>

          <nav className="main__contact-links" aria-label="Contact links">
            <a
              className="main__contact-btn"
              href="https://github.com/chanho-chris-kim"
              target="_blank"
              rel="noreferrer"
              data-analytics="contact"
              data-contact-type="github"
            >
              GitHub
              <span className="sr-only"> (opens in a new tab)</span>
            </a>

            <a
              className="main__contact-btn"
              href="https://www.linkedin.com/in/chanho-chris-kim/"
              target="_blank"
              rel="noreferrer"
              data-analytics="contact"
              data-contact-type="linkedin"
            >
              LinkedIn
              <span className="sr-only"> (opens in a new tab)</span>
            </a>

            <a
              className="main__contact-btn main__contact-btn--primary"
              href="#contact"
              data-analytics="contact"
              data-contact-type="email"
              onClick={(e) => {
                e.preventDefault();
                const user = "contact";
                const domain = "chanhokim.ca";
                window.location.href = `mailto:${user}@${domain}`;
              }}
            >
              Email me
            </a>
          </nav>
        </div>
      </section>

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={closeProjectModal} />
      )}
    </main>
  );
}

export default Main;
